import { useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, CalendarClock, CreditCard, Loader2, MessageSquare, Sparkles, Users } from "lucide-react";
import { MemberList } from "@/components/offers/GroupMembersField";
import { AntiCheatNote } from "@/components/apps/PlanNotes";
import { api } from "@/lib/api";
import { isFramed, openTopLevel } from "@/lib/topNavigation";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";

const iso = (s) => `\u2068${s}\u2069`;
const TONE = { current: "border-[var(--gold)]", paid: "border-emerald-500/70", replaced: "border-neutral-700 opacity-75", expired: "border-neutral-700 opacity-75", archived: "border-neutral-700 opacity-75" };

const Row = ({ label, value, strong, testId }) => (
  <div className={`flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-xs ${strong ? "font-bold text-white" : "text-neutral-300"}`}>
    <span className="min-w-0 break-words">{label}</span><span className="min-w-0 break-words text-end tabular-nums" data-testid={testId}>{value}</span>
  </div>
);

export const useOfferMoney = (currency) => {
  const { locale } = useI18n();
  return (v) => new Intl.NumberFormat(locale, { style: "currency", currency: (currency || "eur").toUpperCase(), minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
};

// Offer card (thread + billing area): rendered from structured data, so it is shown in the customer's language.
export const OfferCard = ({ a, admin = false, onDiscuss, onPaid, testId = "offer-card" }) => {
  const { t, fmtDate, locale } = useI18n();
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState(null);
  const { terms: tm, quote: q, state = "current" } = a;
  const money = useOfferMoney(tm.currency);
  const prepaid = tm.mode === "prepaid";
  const charge = prepaid ? q.total_ttc : Math.round(q.period_ht_cur * 120) / 100;
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/dev/billing/custom", { origin_url: window.location.origin, currency: tm.currency, expected_total: charge });
      if (isFramed()) setUrl(data.checkout_url);
      openTopLevel(data.checkout_url);
      onPaid?.();
    } catch (err) {
      toast.error(translateError(err, t));
      if (err?.response?.status === 409) onPaid?.();  // refreshed amount / state
    } finally {
      setBusy(false);
    }
  };
  const rateAt = a.rate_at || q.fx_at;
  const rateTime = rateAt && new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(rateAt));
  return (
    <div className={`mt-2 min-w-0 space-y-2 rounded-xl border bg-black/40 p-3 ${TONE[state] || TONE.current}`} data-testid={testId}>
      <p className="flex min-w-0 items-start gap-2 text-sm font-bold text-white"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold)]" />
        <span className="min-w-0 break-words">{t(a.group ? "group.title" : a.kind === "minisite" ? "offer.titleMini" : "offer.titleDev")}</span></p>
      {(tm.employees_paid > 0 || tm.employees_offered > 0) && (
        <p className="break-words text-xs font-semibold text-sky-200" data-testid={`${testId}-employees`}>
          {[tm.employees_paid > 0 && t("emp.offerPaid", { n: tm.employees_paid }), tm.employees_offered > 0 && t("emp.offerFree", { n: tm.employees_offered })].filter(Boolean).join(" · ")}
          {a.group ? ` ${t("emp.perBusiness")}` : ""}
        </p>
      )}
      {a.kind === "minisite" && <AntiCheatNote testId={`${testId}-anticheat`} />}
      {a.group && (
        <div className="min-w-0 space-y-1 rounded-lg border border-emerald-500/30 bg-emerald-950/20 p-2" data-testid={`${testId}-group`}>
          <p className="flex min-w-0 items-center gap-1.5 break-words text-xs font-semibold text-emerald-200"><Users className="h-3.5 w-3.5 shrink-0" /> {t("group.included", { n: iso(a.group.members.length) })}</p>
          <MemberList members={a.group.members} testId={`${testId}-group-members`} />
        </div>
      )}
      {a.business && (
        <div className="min-w-0 space-y-1 rounded-lg border border-emerald-500/30 bg-emerald-950/20 p-2" data-testid={`${testId}-business`}>
          <p className="break-words text-xs font-semibold text-emerald-200">{t("biz.cardTitle", { app: a.business.app || "", n: iso(a.business.refs.length) })}</p>
          <p className="text-[0.7rem] text-neutral-300 [overflow-wrap:anywhere]">{a.business.refs.slice(0, 30).map((r) => (a.business.employees?.[r] ? `${r} (+${a.business.employees[r]})` : r)).join(" · ")}{a.business.refs.length > 30 ? " …" : ""}</p>
        </div>
      )}
      <p className="break-words text-xs text-amber-100" data-testid={`${testId}-duration`}>
        {t(tm.free_months ? (tm.free_months === 1 ? "offer.durationFree1" : "offer.durationFree") : "offer.duration", { m: iso(tm.months), f: iso(tm.free_months), total: iso(q.access_months) })}
      </p>
      <p className="break-words text-sm font-semibold text-[var(--gold-bright)]" data-testid={`${testId}-mode`}>
        {prepaid ? t("offer.once", { price: iso(money(q.total_ttc)) }) : t(tm.interval > 1 ? "offer.every" : "offer.monthly", { price: iso(money(charge)), n: iso(tm.interval) })}
      </p>
      <div className="space-y-1">
        <Row label={t(prepaid ? "offer.totalHt" : "offer.periodHt")} value={money(prepaid ? q.total_ht : q.period_ht_cur)} testId={`${testId}-ht`} />
        <Row label={t("offer.vat")} value={money(prepaid ? q.total_vat : charge - q.period_ht_cur)} testId={`${testId}-vat`} />
        <Row label={t("offer.ttc")} value={money(charge)} strong testId={`${testId}-ttc`} />
        {!prepaid && q.charges > 1 && <Row label={t("offer.durationTotal", { n: iso(q.charges) })} value={money(q.total_ttc)} testId={`${testId}-total`} />}
        {tm.discount_pct > 0 && tm.discount_months > 0 && <Row label={t("offer.discount", { p: iso(tm.discount_pct), m: iso(tm.discount_months) })} value={`− ${money(q.discount_ht * (tm.currency === "usd" && q.fx_rate ? q.fx_rate : 1))}`} testId={`${testId}-discount`} />}
        {a.kind !== "minisite" && a.apps && <Row label={t("offer.apps")} value={new Intl.NumberFormat(locale).format(a.apps)} testId={`${testId}-apps`} />}
      </div>
      {tm.currency === "usd" && <p className="break-words text-[0.7rem] text-neutral-400" data-testid={`${testId}-fx`}>{t("offer.fx", { time: iso(rateTime || "—") })}</p>}
      <p className="flex min-w-0 items-start gap-1.5 break-words text-xs text-neutral-300" data-testid={`${testId}-dates`}>
        <CalendarClock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--gold)]" />
        <span className="min-w-0">{t("offer.dates", { start: iso(fmtDate(q.start || a.created_at)), end: iso(fmtDate(q.end)) })}
          {q.free_until && !prepaid && <> · {t("offer.firstCharge", { date: iso(fmtDate(q.free_until)) })}</>}</span>
      </p>
      <p className="break-words text-[0.7rem] text-neutral-400">{t(prepaid ? "offer.endPrepaid" : "offer.endRecurring")} {t("offer.cancelRule")}</p>
      {state === "paid" && <p className="flex items-center gap-1.5 text-xs font-semibold text-emerald-300" data-testid={`${testId}-paid`}><BadgeCheck className="h-4 w-4" /> {t("offer.paid", { date: iso(fmtDate(a.paid_at)), until: iso(fmtDate(a.paid_until || q.end)) })}</p>}
      {state === "replaced" && <p className="text-xs font-semibold text-neutral-400" data-testid={`${testId}-replaced`}>{t("offer.replaced")}</p>}
      {state === "expired" && <p className="text-xs font-semibold text-red-300" data-testid={`${testId}-expired`}>{t("offer.expired")}</p>}
      {state === "current" && a.expires_at && <p className="text-[0.7rem] text-amber-200" data-testid={`${testId}-valid`}>{t("offer.validUntil", { date: iso(fmtDate(a.expires_at)) })}</p>}
      {!admin && state === "current" && (
        <div className="flex flex-wrap gap-2 pt-1">
          <button type="button" className="btn-gold btn-cta max-w-full whitespace-normal text-start" disabled={busy} onClick={pay} data-testid={`${testId}-pay-btn`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4 shrink-0" />} {t("offer.pay")}
          </button>
          {onDiscuss && <button type="button" className="btn-red btn-sm max-w-full whitespace-normal text-start" onClick={onDiscuss} data-testid={`${testId}-discuss-btn`}>
            <MessageSquare className="h-4 w-4 shrink-0" /> {t("offer.discuss")}
          </button>}
        </div>
      )}
      {url && <a href={url} target="_top" rel="noopener" className="btn-red btn-sm" data-testid={`${testId}-open-checkout`}>{t("billing.openCheckout")}</a>}
    </div>
  );
};

export const ThreadTypeBadge = ({ type, testId }) => {
  const { t } = useI18n();
  const tone = { dev_plan: "bg-amber-500/15 text-amber-200", minisite_plan: "bg-emerald-500/15 text-emerald-200", question: "bg-sky-500/15 text-sky-200" }[type];
  if (!tone) return null;
  return <span className={`max-w-full shrink-0 break-words rounded-full px-2 py-0.5 text-[0.65rem] font-bold uppercase ${tone}`} data-testid={testId}>{t(`msg.type.${type}`)}</span>;
};
